"use client";

import { useRef, useState } from "react";
import NextImage from "next/image";
import { motion } from "framer-motion";
import type { MediaPlayerInstance } from "@vidstack/react";

import { cn } from "@/lib/utils";

import { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";
import { PageReplaySchema } from "@/sanity/pages/PageReplayTemplate";
import { SanityVideoPlayer, hasPlayableSource } from "@/components/ui/SanityVideoPlayer";
import {
  CAP_TRIM,
  CompactCountdown,
  EventHeroBackdrop,
  EventNavbar,
  TicketCTA,
} from "@/components/pageComponents/event/EventShared";

import statue from "@/public/replay/statue-horse.png";
import videoBg from "@/public/replay/video-bg.png";
import arrow from "@/public/replay/arrow.svg";
import play from "@/public/replay/play.svg";

const ease = [0.25, 0.1, 0.25, 1] as const;
/** Entering motion shared by the stacked hero content (same as the event hero). */
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};
const enter = (delay: number) => ({ duration: 0.5, ease, delay });

/** White wash + light-green floor over the poster still (Figma 8610:788). */
const POSTER_GRADIENT =
  "linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 140.47%), linear-gradient(0deg, #e6edeb 0%, rgba(230, 237, 235, 0) 14.194%)";

/**
 * Glass-framed replay player. The Sanity player is mounted underneath from the
 * start; the Figma poster composition (or the video's own thumbnail) and the
 * pill play button sit on top until the pill is pressed. Playback starts
 * synchronously inside that tap so iOS/Safari accept unmuted play.
 */
function ReplayVideoCard({ video, playLabel }: { video?: IMuxVideoPlayer; playLabel?: string }) {
  const playerRef = useRef<MediaPlayerInstance | null>(null);
  const [playing, setPlaying] = useState(false);
  const hasVideo = hasPlayableSource(video);
  const thumbnail = video?.thumbnail?.src;

  /** Set while a play() attempt was made before the media was ready. */
  const pendingPlay = useRef(false);

  const onPlay = () => {
    if (!hasVideo) return;
    setPlaying(true);
    const player = playerRef.current;
    if (!player) return;
    // The media may still be loading; ask for it, then start inside this gesture
    // (iOS/Safari only allow unmuted playback here) and retry once it can play.
    player.startLoading();
    pendingPlay.current = true;
    player
      .play()
      .then(() => {
        pendingPlay.current = false;
      })
      .catch(() => {});
  };

  const onCanPlay = () => {
    if (!pendingPlay.current) return;
    pendingPlay.current = false;
    playerRef.current?.play().catch(() => {});
  };

  return (
    <div className="flex w-full flex-col items-start rounded-[0.87rem] border-[0.073rem] border-white/[0.38] bg-white/10 p-[0.44rem] backdrop-blur-[1.02rem] lg:w-auto lg:rounded-[1.82rem] lg:border-[0.15rem] lg:p-[0.91rem] lg:backdrop-blur-[2.12rem]">
      <div className="relative h-[27.41rem] w-full overflow-clip rounded-[0.44rem] lg:h-[57.17rem] lg:w-[101.63rem] lg:rounded-[0.91rem]">
        {hasVideo && video && (
          <SanityVideoPlayer
            ref={playerRef}
            playerData={video}
            playsinline
            // Our pill is the only play control until playback starts; afterwards the
            // player's own button is the normal pause affordance.
            hideBigPlayButton={!playing}
            onCanPlay={onCanPlay}
            className="!h-full !w-full !rounded-[inherit]"
          />
        )}
        {/* Poster + pill sit above the player's own chrome (its overlay is z-20) until playback starts */}
        {!playing && (
          <div className="absolute inset-0 z-30">
            {thumbnail ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={thumbnail}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <>
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0">
                  <NextImage
                    src={videoBg}
                    alt=""
                    className="absolute inset-0 !h-full !w-full !max-w-none object-cover opacity-35"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ backgroundImage: POSTER_GRADIENT }}
                  />
                </div>
                <div
                  aria-hidden
                  className="pointer-events-none absolute top-[-0.8rem] left-[calc(50%+0.97rem)] h-[30.47rem] w-[50.62rem] -translate-x-1/2 lg:top-[-1.67rem] lg:left-[calc(50%+2.03rem)] lg:h-[63.53rem] lg:w-[105.57rem]">
                  <NextImage
                    src={statue}
                    alt=""
                    priority
                    className="absolute inset-0 !h-full !w-full !max-w-none object-cover object-bottom"
                  />
                </div>
              </>
            )}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-[#021913]/[0.14]"
            />

            {/* Pill play button */}
            <button
              type="button"
              onClick={onPlay}
              disabled={!hasVideo}
              aria-label={playLabel || "Play"}
              className="group absolute top-[10.12rem] left-[calc(50%+1rem)] flex -translate-x-1/2 cursor-pointer flex-col items-start rounded-[10.58rem] border-[0.106rem] border-white/30 bg-white/10 p-[0.42rem] backdrop-blur-[1.89rem] disabled:cursor-default lg:top-[23.69rem] lg:left-1/2 lg:w-[30.71rem] lg:rounded-[12.5rem] lg:border-[0.125rem] lg:p-[0.5rem] lg:backdrop-blur-[2.23rem]">
              <span
                className={cn(
                  "flex w-full items-center justify-center gap-[1.26rem] rounded-[4.13rem] border-[0.084rem] border-white/[0.19] bg-white px-[2.5rem] py-[1.75rem] transition-transform duration-300 ease-out lg:gap-[1.49rem] lg:rounded-[4.88rem] lg:border-[0.1rem] lg:px-[3.66rem] lg:py-[2.14rem]",
                  hasVideo && "group-hover:scale-[1.03]",
                )}>
                <span className="relative block h-[2.14rem] w-[2.14rem] shrink-0 lg:h-[2.9rem] lg:w-[2.9rem]">
                  <span className="absolute inset-0 block -scale-y-100 -rotate-90">
                    <span className="absolute inset-[6.19%_11.22%_25%_11.22%] block">
                      <NextImage
                        src={play}
                        alt=""
                        aria-hidden
                        className="block !h-full !w-full !max-w-none"
                      />
                    </span>
                  </span>
                </span>
                {playLabel && (
                  <span className="font-geist text-[1.75rem] leading-[1.3] font-normal tracking-[0.0175rem] whitespace-nowrap text-black lg:text-[2.25rem] lg:tracking-[0.0225rem]">
                    {playLabel}
                  </span>
                )}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Dark replay hero: backdrop, navbar, badge + title, glass video card, offer copy, CTA + compact countdown. */
export function ReplayHero({ data }: { data: PageReplaySchema }) {
  const showOffer = data.hideOffer === false;

  return (
    <section
      className={cn(
        "relative w-full overflow-clip bg-[#10322a] pb-[6.5rem]",
        showOffer && "lg:min-h-[148rem]",
        "lg:rounded-b-[1.25rem] lg:pb-[9.5rem]",
      )}>
      {/* ── Decorative background — blurs in on load ─────────────────── */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, filter: "blur(8px)" }}
        animate={{ opacity: 1, filter: "blur(0px)" }}
        transition={{ duration: 0.7, ease }}
        className="pointer-events-none absolute inset-0">
        <EventHeroBackdrop
          candlePositions={[
            "left-[-3.5rem] top-[75.625rem] hidden lg:block",
            "right-[-8.875rem] top-[32rem] hidden lg:block",
          ]}
          wavePosition="top-[44.875rem] left-[-29.25rem] lg:top-[56.625rem] lg:left-[-7.75rem]"
        />
      </motion.div>

      <EventNavbar
        data={data.navCta}
        textMobile={data.navCtaTextMobile}
      />

      {/* ── Hero content ─────────────────────────────────────────────── */}
      <div className="relative z-10 mx-auto flex w-[49.75rem] flex-col items-center pt-[14.375rem] lg:w-[139.5rem] lg:pt-[18.25rem]">
        <div className="relative flex w-full flex-col items-center gap-[2.5rem] lg:gap-[3rem]">
          {data.badgeText && (
            <motion.div
              {...fadeUp}
              transition={enter(0.1)}
              className="flex items-center gap-[1.25rem] border border-white/10 bg-white/[0.04] px-[2.5rem] py-[1.5rem]">
              <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#228e71]" />
              <p
                className={cn(
                  "mb-0 font-geist text-[1.75rem] leading-[1.146] font-medium whitespace-nowrap text-white uppercase",
                  CAP_TRIM,
                )}>
                {data.badgeText}
              </p>
            </motion.div>
          )}

          <div className="flex flex-col items-center gap-[3rem] lg:gap-[4rem]">
            {(data.titleItalic || data.titleRegular) && (
              <motion.h1
                {...fadeUp}
                transition={enter(0.15)}
                className={cn(
                  "m-0 w-[39rem] text-center font-blacker text-[4.75rem] leading-[1.12] font-normal tracking-[-0.1425rem] text-white lg:w-auto lg:text-[7rem] lg:tracking-[-0.21rem] [&_span]:font-blacker",
                  CAP_TRIM,
                )}>
                {data.titleItalic && <span className="text-[#cfffdb] italic">{data.titleItalic}</span>}
                {data.titleItalic && data.titleRegular && <br />}
                {data.titleRegular && <span>{data.titleRegular}</span>}
              </motion.h1>
            )}
            {data.description && (
              /* 398px-wide box (Figma 8610:754 / 8610:3803) so the arrow offsets below are exact */
              <div className="relative w-full lg:w-[49.75rem]">
                <motion.p
                  {...fadeUp}
                  transition={enter(0.25)}
                  className={cn(
                    "mb-0 w-full text-center font-geist text-[2rem] leading-[1.6] font-normal text-white opacity-80 lg:text-[2.25rem]",
                    CAP_TRIM,
                  )}>
                  {data.description}
                </motion.p>
                {/* Hand-drawn arrow hanging off the end of the line (Figma 8610:745 / 8610:3816) */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute top-[4.05rem] left-[50.585rem] flex h-[3.92rem] w-[5.07rem] items-center justify-center lg:top-0 lg:left-[42.03rem] lg:h-[5.04rem] lg:w-[7.07rem]">
                  <div className="-scale-y-100 rotate-[-70.8deg] lg:rotate-[-77.6deg]">
                    <div className="relative h-[4.46rem] w-[2.6rem] lg:h-[6.41rem] lg:w-[3.75rem]">
                      <NextImage
                        src={arrow}
                        alt=""
                        className="absolute inset-[-1.34%_-1.12%_0_-1.74%] block !h-full !w-full !max-w-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <motion.div
          {...fadeUp}
          transition={enter(0.35)}
          className="mt-[3rem] flex w-full justify-center lg:mt-[5.375rem]">
          <ReplayVideoCard
            video={data.video}
            playLabel={data.playButtonText}
          />
        </motion.div>

        {data.offerText && (
          <motion.p
            {...fadeUp}
            transition={enter(0.45)}
            className={cn(
              "mt-[2.8rem] mb-0 w-[46.75rem] text-center font-geist text-[2.25rem] leading-[1.5] font-normal whitespace-pre-line text-black opacity-80 lg:mt-[3.5rem] lg:w-auto lg:leading-[1.6]",
              CAP_TRIM,
            )}>
            {data.offerText}
          </motion.p>
        )}

        <motion.div
          {...fadeUp}
          transition={enter(0.55)}
          className="mt-[3rem] flex w-[44.375rem] flex-col items-center gap-[2rem] lg:mt-[2.875rem] lg:gap-[2.25rem]">
          <TicketCTA
            data={data.cta}
            className="w-full"
            textClassName="text-[2.25rem]"
          />
          {showOffer && (
            <CompactCountdown
              target={data.countdownTarget}
              variant="light"
            />
          )}
          {showOffer && data.anchorText && (
            <a
              href="#angebot"
              className={cn("font-geist text-[2.25rem] leading-[1.6] font-normal text-black underline", CAP_TRIM)}>
              {data.anchorText}
            </a>
          )}
        </motion.div>
      </div>
    </section>
  );
}
